import { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";

interface FloatingLogo {
  src: string;
  alt: string;
}

interface AlternativesHeroProps {
  trustBadge: string;
  title: string;
  description: string;
  descriptionHighlights?: string[];
  floatingLogos?: FloatingLogo[];
}

const LOGO_POSITIONS = [
  "left-4 top-20 md:left-12 md:top-24",
  "right-4 top-8 md:right-16 md:top-12",
  "left-1/2 top-[52%] -translate-x-1/2 md:left-[73%]",
  "left-4 bottom-20 md:left-16 md:bottom-24",
  "right-4 bottom-16 md:right-12 md:bottom-20",
] as const;

const DOT_POSITIONS = [
  "left-[18%] top-[15%] h-2 w-2",
  "right-[22%] top-[30%] h-1.5 w-1.5",
  "left-[12%] bottom-[35%] h-2.5 w-2.5",
  "right-[15%] bottom-[18%] h-2 w-2",
  "left-[35%] top-[25%] h-1.5 w-1.5",
  "right-[38%] bottom-[30%] h-1.5 w-1.5",
] as const;

function highlightDescription(text: string, highlights: string[]): React.ReactNode {
  if (highlights.length === 0) return text;

  const escapedHighlights = highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escapedHighlights.join("|")})`, "g");
  const parts = text.split(pattern);

  return parts.map((part, i) => {
    if (highlights.includes(part)) {
      return (
        <strong key={i} className="font-semibold text-ash-900">
          {part}
        </strong>
      );
    }
    return part;
  });
}

export function AlternativesHero({
  trustBadge,
  title,
  description,
  descriptionHighlights = [],
  floatingLogos = [],
}: AlternativesHeroProps) {
  const renderedDescription = useMemo(
    () => highlightDescription(description, descriptionHighlights),
    [description, descriptionHighlights]
  );

  return (
    <section className="relative overflow-hidden bg-ash-100 pt-32 pb-20 md:pt-40 md:pb-28">
      {/* Decorative orange dots */}
      {DOT_POSITIONS.map((pos, i) => (
        <span key={i} className={`absolute hidden rounded-full bg-rebolt-500/25 md:block ${pos}`} />
      ))}

      {/* Floating competitor logos */}
      {floatingLogos.slice(0, 5).map((logo, i) => (
        <div key={logo.alt} className={`absolute hidden md:block ${LOGO_POSITIONS[i]}`}>
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-md lg:h-16 lg:w-16">
            <Image
              src={logo.src}
              alt={logo.alt}
              width={40}
              height={40}
              className="h-8 w-8 object-contain lg:h-10 lg:w-10"
            />
          </div>
        </div>
      ))}

      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <p className="mb-6 inline-block rounded-full bg-rebolt-500/10 px-4 py-1.5 text-sm font-semibold tracking-wide text-rebolt-600">
          {trustBadge}
        </p>

        <h1 className="font-clash text-4xl font-medium leading-[1.05] tracking-snug text-ash-900 md:text-6xl">
          {title}
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ash-500 md:text-xl">{renderedDescription}</p>

        <div className="mt-8">
          <Link
            href="https://cal.com/team/builtright/launch"
            className="inline-flex items-center rounded-full bg-rebolt-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-rebolt-600"
          >
            Get More Business
          </Link>
        </div>
      </div>
    </section>
  );
}
